import { useQuery } from '@tanstack/react-query';
import { getDashboardAnalytics } from '@services/analytics.service';
import { useAuthStore } from '@stores/auth.store';
import { isOwnerRole } from '@utils/routing';

export const DASHBOARD_ANALYTICS_QUERY_KEY = ['analytics', 'dashboard'];

const toNumberOrZero = (v) => {
    const n = typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
    return Number.isFinite(n) ? n : 0;
};

const toCurrency = (value) => {
    const n = toNumberOrZero(value);
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
    } catch {
        // Fallback for environments missing Intl/currency data
        return `$${n.toLocaleString()}`;
    }
};

const toPercent = (value) => {
    if (typeof value === 'string') {
        const t = value.trim();
        if (t.endsWith('%')) return t;
        const n = toNumberOrZero(t);
        return `${n}%`;
    }
    const n = toNumberOrZero(value);
    return `${n}%`;
};

const toScore = (value) => {
    if (value == null || value === '') return 0;
    const n = toNumberOrZero(value);
    // keep one decimal if provided by backend
    return Number.isInteger(n) ? n : Number(n.toFixed(1));
};

/**
 * Fetches dashboard analytics (stats, recent notifications, recent projects, activity logs).
 */
export function useDashboardAnalytics() {
    const role = useAuthStore((s) => s.role ?? s.user?.role ?? (Array.isArray(s.roles) && s.roles.length ? s.roles[0] : null));
    const roles = useAuthStore((s) => s.roles ?? (Array.isArray(s.user?.roles) ? s.user.roles : []));
    const isAdminLike = [role, ...(Array.isArray(roles) ? roles : [])].some((r) => isOwnerRole(String(r || '')));
    const requestedView = isAdminLike ? 'admin' : 'client';

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: [...DASHBOARD_ANALYTICS_QUERY_KEY, { view: requestedView }],
        queryFn: () => getDashboardAnalytics({ view: requestedView }),
        staleTime: 60 * 1000,
    });

    const stats = data?.stats ?? {};
    const view = data?.view ?? requestedView ?? 'admin';

    let kpiCards = [];
    switch (view) {
        case 'client':
            kpiCards = [
                { key: 'activeProjects', label: 'Active Projects', value: toNumberOrZero(stats.activeProjects), icon: 'folder' },
                { key: 'completedProjects', label: 'Completed Projects', value: toNumberOrZero(stats.completedProjects), icon: 'check' },
                { key: 'spendings', label: 'Spendings', value: toCurrency(stats.spendings), icon: 'zap' },
                { key: 'projectCompletion', label: 'Completion %', value: toPercent(stats.projectCompletionPercentage), icon: 'gauge' },
                { key: 'pendingRevisions', label: 'Pending Revisions', value: toNumberOrZero(stats.pendingRevisions), icon: 'list' },
            ];
            break;
        case 'pm':
            kpiCards = [
                { key: 'activeProjects', label: 'Active Projects', value: toNumberOrZero(stats.activeProjects), icon: 'folder' },
                { key: 'completedProjects', label: 'Completed Projects', value: toNumberOrZero(stats.completedProjects), icon: 'check' },
                { key: 'totalTasks', label: 'Sprint Tasks', value: toNumberOrZero(stats?.sprintProgress?.totalTasks), icon: 'list' },
                { key: 'sprintCompletion', label: 'Sprint Completion', value: toPercent(stats?.sprintProgress?.completionPercentage), icon: 'gauge' },
                { key: 'openBlockers', label: 'Open Blockers', value: toNumberOrZero(stats.openBlockers), icon: 'alert' },
                { key: 'milestoneDelays', label: 'Milestone Delays', value: toNumberOrZero(stats.milestoneDelays), icon: 'clock' },
            ];
            break;
        case 'general':
            kpiCards = [
                { key: 'activeProjects', label: 'Active Projects', value: toNumberOrZero(stats.activeProjects), icon: 'folder' },
                { key: 'todo', label: 'To Do', value: toNumberOrZero(stats.myTasksTodo), icon: 'list' },
                { key: 'inProgress', label: 'In Progress', value: toNumberOrZero(stats.myTasksInProgress), icon: 'clock' },
                { key: 'completed', label: 'Completed', value: toNumberOrZero(stats.myTasksCompleted), icon: 'check' },
                { key: 'returnedBugs', label: 'Returned Bugs', value: toNumberOrZero(stats.myReturnedBugs), icon: 'bug' },
                { key: 'bugPercentage', label: 'Bug Rate', value: toPercent(stats.bugPercentage), icon: 'gauge' },
            ];
            break;
        case 'qa':
            kpiCards = [
                { key: 'activeProjects', label: 'Active Projects', value: toNumberOrZero(stats.activeProjects), icon: 'folder' },
                { key: 'pendingValidations', label: 'Pending Validations', value: toNumberOrZero(stats.pendingValidations), icon: 'clock' },
                { key: 'openBlockers', label: 'Open Blockers', value: toNumberOrZero(stats.openBlockers), icon: 'alert' },
                { key: 'sprintCompletion', label: 'Sprint Completion', value: toPercent(stats?.sprintProgress?.completionPercentage), icon: 'gauge' },
                { key: 'totalBugs', label: 'Total Bugs', value: toNumberOrZero(stats?.fixRatio?.totalBugs), icon: 'bug' },
                { key: 'fixRate', label: 'Fix Rate', value: toPercent(stats?.fixRatio?.fixPercentage), icon: 'check' },
            ];
            break;
        case 'admin':
        default:
            kpiCards = [
                { key: 'activeProjects', label: 'Active Projects', value: toNumberOrZero(stats.activeProjects), icon: 'folder' },
                { key: 'activeClients', label: 'Active Clients', value: toNumberOrZero(stats.activeClients), icon: 'users' },
                { key: 'activeEmployees', label: 'Active Employees', value: toNumberOrZero(stats.activeEmployees), icon: 'users' },
                { key: 'revenue', label: 'Revenue', value: toCurrency(stats.revenue), icon: 'zap' },
                { key: 'totalProjects', label: 'Total Projects', value: toNumberOrZero(stats.totalProjects), icon: 'folder' },
                { key: 'delayedProjects', label: 'Delayed Projects', value: toNumberOrZero(stats.delayedProjects), icon: 'clock' },
                { key: 'avgTasks', label: 'Avg Tasks/Emp', value: toNumberOrZero(stats?.employeeWorkload?.averageTasksPerEmployee), icon: 'list' },
                { key: 'avgSprintTasks', label: 'Avg Sprint Tasks', value: toNumberOrZero(stats?.sprintVelocity?.averageCompletedTasksPerSprint), icon: 'gauge' },
                { key: 'openBugs', label: 'Open Bugs', value: toNumberOrZero(stats?.bugRatio?.openBugs), icon: 'bug' },
            ];
            break;
    }

    return {
        stats,
        recentNotifications: data?.recentNotifications ?? [],
        recentProjects: data?.recentProjects ?? [],
        activityLogs: data?.activityLogs ?? [],
        view,
        kpiCards,
        isLoading,
        error,
        refetch,
    };
}
